import React, { useState } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { REVIEWS } from "@/data/products";
import { motion, AnimatePresence } from "framer-motion";

export function ReviewsSection() {
  const [index, setIndex] = useState(0);

  const prev = () =>
    setIndex((i) => (i - 1 + REVIEWS.length) % REVIEWS.length);
  const next = () => setIndex((i) => (i + 1) % REVIEWS.length);

  const r = REVIEWS[index];

  return (
    <section className="bg-white py-24" data-testid="reviews-section">
      <div className="container-wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="section-label mb-4">Client Reviews</div>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl text-vortex-navy tracking-tighter leading-[1.05] mb-6">
              What Chennai&apos;s power engineers say.
            </h2>
            <p className="text-vortex-slate leading-relaxed mb-8">
              Over 12,500 installations across manufacturing, healthcare, IT,
              textile and commercial sectors. Read what our long-term partners
              have to say.
            </p>

            <div className="flex items-center gap-3 mb-6">
              <div className="flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5 fill-vortex-orange text-vortex-orange"
                    strokeWidth={0}
                  />
                ))}
              </div>
              <div className="font-heading font-bold text-vortex-navy">
                4.9 / 5
              </div>
              <div className="text-sm text-vortex-slate">
                · 300+ Google Reviews
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={prev}
                aria-label="Previous review"
                className="w-11 h-11 rounded-full border border-vortex-border hover:bg-vortex-navy hover:text-white transition-colors flex items-center justify-center"
                data-testid="reviews-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                aria-label="Next review"
                className="w-11 h-11 rounded-full border border-vortex-border hover:bg-vortex-navy hover:text-white transition-colors flex items-center justify-center"
                data-testid="reviews-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="ml-3 text-sm font-semibold text-vortex-slate">
                0{index + 1} <span className="text-vortex-slate/50">/ 0{REVIEWS.length}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={r.name}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.4 }}
                className="relative bg-vortex-alt border border-vortex-border rounded-3xl p-8 md:p-10"
                data-testid={`review-card-${index}`}
              >
                <Quote
                  className="absolute top-6 right-6 w-16 h-16 text-vortex-blue/20"
                  strokeWidth={1}
                />
                <div className="flex mb-5">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 fill-vortex-orange text-vortex-orange"
                      strokeWidth={0}
                    />
                  ))}
                </div>
                <p className="font-heading text-xl md:text-2xl text-vortex-navy leading-snug mb-8">
                  &ldquo;{r.text}&rdquo;
                </p>
                <div className="flex items-center gap-4 pt-6 border-t border-vortex-border">
                  <div className="w-12 h-12 rounded-full bg-vortex-navy text-white font-heading font-bold flex items-center justify-center">
                    {r.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)}
                  </div>
                  <div>
                    <div className="font-heading font-bold text-vortex-navy">
                      {r.name}
                    </div>
                    <div className="text-sm text-vortex-slate">{r.role}</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
